import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import RequiredMark from '../../../shared/RequiredMark';
import CancelButton from '../../../shared/CancelButton';
import LineOrderSwitch, { bySku } from '../../../shared/LineOrderSwitch';
import SaveButton from '../../../shared/SaveButton';
import ReportOverlay from '../../../shared/ReportOverlay';
import ActionsMenu from '../../../shared/ActionsMenu';
import NextStepButton from '../../../shared/NextStepButton';
import useLineReorder from '../../../shared/useLineReorder';
import ConfirmDialog from '../../../shared/ConfirmDialog';
import Accordion from '../../../shared/Accordion';
import DuplicateBanner from '../../../shared/DuplicateBanner';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import '../../../shared/DetailTimelineLayout.css';
import QuoteReportView from './QuoteReportView';
import ComposeEmailDrawer from '../../crm/Leads/ComposeEmailDrawer';
import QuoteTimeline from './QuoteTimeline';
import QuoteShippingEstimate, { estimateInputsKey, chargeTypedOver, shippingNotAdded, ShippingOverrideReason } from './QuoteShippingEstimate';
import { isAustraliaPost } from '../../warehouse/packageShared';
import { productLabel, customerLabel, formatPrice, formatUnitPrice } from '../Prices/priceLookupShared';
import { AvailabilityPill, CategoryPill, NeedsWeightPill, RetailFallbackPill } from '../../../shared/Pill';
import { useAnchorPriceGroupName } from '../../../shared/useAnchorPriceGroupName';
import PriceCalculator from '../../../shared/PriceCalculator';
import LineUnitPriceField from '../../../shared/LineUnitPriceField';
import OverrideRevertBadge from '../../../shared/OverrideRevertBadge';
import OrderDiscountRow, { useOrderDiscount } from '../shared/OrderDiscount';
import { basisOf, priceLine, shownUnitPrice, shownLineAmount, documentTotals } from '../../../shared/pricingBasis';
import GstAwareCostField from '../../../shared/GstAwareCostField';
import ShippingOptionsPicker, { computeDefaultCost } from '../../../shared/ShippingOptionsPicker';
import { carrierChoices, servicesForCarrier, carrierForService } from '../../../shared/shippingCarriage';
import AddressPicker, { fetchDeliveryAddresses } from '../../../shared/AddressPicker';
import { Building2, Info, UserRound } from 'lucide-react';
import RecordLookupField from '../../../shared/RecordLookupField';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';
import NumberInput from '../../../shared/NumberInput';
import { formatNumber, formatMoney } from '../../../shared/numberFormat';
import { useScreenAccess, usePermissions } from '../../../shared/PermissionsContext';
import { joinShippingNotes, fillQuoteReminder, quoteValidUntil } from '../../../shared/quotePriceReminder';
import DocumentComments from '../shared/DocumentComments';
import DocumentPayments, { PaymentStatusPill } from '../shared/DocumentPayments';
import useReestimateOnSave from '../shared/useReestimateOnSave';
import useSaveKey from '../../../shared/useSaveKey';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { SegmentedToggle } from '../../../shared/DashboardChart';
import { GST_VIEWS, viewUnitPrice, viewLineAmount, viewSubtotal, viewDiscount } from '../../../shared/gstView';
import { askConfirm, showMessage } from '../../../shared/AppDialogs';

// Build/edit a Quote as one buffered document — Customer + every line are
// held in local state and nothing reaches the server until Save, which
// writes the whole header+lines set in one call (POST for a new Quote, PUT
// for an existing Draft — see quoteController.js). Cancel just discards
// local state and returns to the list; nothing was ever partially
// persisted, so there's nothing to undo server-side. Each line's price is
// previewed live via the same /api/price-lookup Price Lookup itself uses,
// then re-resolved fresh by the server at Save time — a Save-time capture,
// not a live figure, per the Quotes vision's Firm Price promise. Only
// Draft exists yet; Send and the rest of the lifecycle are later stories.
let rowKeySeq = 0;
function nextRowKey() {
  rowKeySeq += 1;
  return rowKeySeq;
}

function blankDraftRow() {
  return {
    productSearch: '', productId: '', product: null,
    results: [], showResults: false,
    uomOptions: [], uomId: '', qty: '1',
  };
}

const STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  expired: 'Expired',
};

// Pinned rather than left to CategoryPill's default hash — Withdrawn used
// to land on the same green as Converted purely by hash coincidence, which
// read as "success" for a terminal/negative status. Converted keeps that
// green (it genuinely is the good outcome); Withdrawn takes the color
// Converted's hash used to have instead. Every other status is frozen at
// whatever its hash already gave it, so this reads as no visible change
// for them.
const QUOTE_STATUS_COLORS = {
  draft: '#7b1fa2',
  sent: '#1976d2',
  accepted: '#455a64',
  rejected: '#5d4037',
  withdrawn: '#c2185b',
  expired: '#ef6c00',
};
const CONVERTED_COLOR = '#2e7d32';

function QuotePage() {
  const { canCreate, canUpdate } = useScreenAccess();
  const { accessFor } = usePermissions();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();

  const [loading, setLoading] = useState(!!id || !!location.state?.copyFromId);
  const [saving, setSaving] = useState(false);
  const [notFound, setNotFound] = useState(false);
  // 'ex' / 'inc' once the Ex GST / Inc GST switch is used; null follows
  // the Customer's Pricing Basis.
  const [gstView, setGstView] = useState(null);
  const [quoteMeta, setQuoteMeta] = useState(null); // { display_number, status, createdAt }
  const [showReport, setShowReport] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  const [customerId, setCustomerId] = useState('');
  // The picked Customer as the Customer field shows it ({ id, label, type }).
  const [customer, setCustomer] = useState(null);

  // Address is one of the selected Customer's delivery addresses — its own,
  // or a linked Individual's (live reference, not a copy — see
  // deliveryAddressService.js) — so its options are always whichever
  // Customer is currently selected, not a standalone picker.
  const [addressId, setAddressId] = useState('');
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [poReference, setPoReference] = useState('');
  const [customerComment, setCustomerComment] = useState('');
  const [staffComment, setStaffComment] = useState('');
  // Pre-filled from the Customer's own default shipping_service_id when a
  // Customer is selected (applyCustomer below), then freely overridable —
  // same "stored default flows in, edit doesn't write back" shape as
  // resolveShippingService on the server (issue #183).
  // Carrier picked ahead of the Service (issue #281) — changing it clears
  // the Service, which clears the Options (ShippingOptionsPicker).
  const [carrierId, setCarrierId] = useState('');
  const [shippingServiceId, setShippingServiceId] = useState('');
  const [shippingServices, setShippingServices] = useState([]);
  // Tenant's configured unit for Product/UoM Weight (see
  // ProductWeightsPage.js) — g or kg, used only to label the Total Weight
  // figure below; the raw values summed are already in that same unit.
  const [weightUnit, setWeightUnit] = useState('');
  // Manually entered (issue #196) — always held here as ex-GST (matching
  // how it's stored — see quoteController.js's parseCost), null when not
  // yet entered. Displayed/typed as Inc-GST whenever GST applies — see
  // GstAwareCostField.js.
  const [shippingCost, setShippingCost] = useState(null);
  const [shippingOverrideReason, setShippingOverrideReason] = useState('');
  const discount = useOrderDiscount();
  // Selected Shipping Options (issue #193) — [{ shipping_option_id, cost,
  // overridden }], scoped to whichever Service is currently selected (see
  // ShippingOptionsPicker.js). Replaces the old standalone Insurance Type/
  // Insurance Cost pair entirely.
  const [selectedOptions, setSelectedOptions] = useState([]);
  // Australia Post shipping estimate (issue #327): the Packaging it's sized
  // by, and the last Estimate Shipping result (saved with the Quote).
  const [estimatePackagingId, setEstimatePackagingId] = useState('');
  const [shippingEstimate, setShippingEstimate] = useState(null);
  const [packagings, setPackagings] = useState([]);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState('');
  // The tenant's wording for an estimated charge, shown in Notes as Print does.
  const [estimateWording, setEstimateWording] = useState('');
  // The tenant's own price reminder (Sales Settings › Quotes), blank for none.
  const [priceReminder, setPriceReminder] = useState('');
  const [validDays, setValidDays] = useState(null);
  // Tax Handling Type — same cascade QuoteReportView.js reads off
  // quote.customer, kept here too so the edit screen's totals match what
  // Print will show rather than always assuming ex-GST. See
  // vis_quotes.md's GST / Tax Display on a Quote section.
  const [customerTax, setCustomerTax] = useState(null);

  const anchorGroupName = useAnchorPriceGroupName();
  const [lines, setLines] = useState([]); // committed rows: { key, productId, product, uomOptions, uomId, qty, unitPrice, extendedPrice, referencePrice }
  const [draftRow, setDraftRow] = useState(blankDraftRow());
  const draftSearchTimer = useRef(null);

  // Shared by the initial load and the "Refresh Quote" button — GET
  // already re-resolves a Draft's prices *and* GST server-side (same
  // resolveQuoteLines call — see quoteController.js), so re-running this
  // same fetch is all a manual refresh needs to do. One action, not a
  // separate "refresh tax": the two are never resolved independently.
  const loadQuote = async () => {
    const res = await axios.get(`/api/quotes/${id}`);
    const quote = res.data.data;
    setQuoteMeta({
      display_number: quote.display_number,
      status: quote.status,
      createdAt: quote.createdAt,
      quoteDate: quote.quote_date,
      validUntil: quote.valid_until,
      sentAt: quote.sent_at,
      acceptedAt: quote.accepted_at,
      withdrawnAt: quote.withdrawn_at,
      rejectedAt: quote.rejected_at,
      expiredAt: quote.expired_at,
      convertedSalesOrder: quote.converted_sales_order,
      copiedFrom: quote.copied_from,
      originLabel: quote.origin_label,
      createdByName: quote.created_by_name,
    });
    setCustomerId(String(quote.customer.id));
    setCustomer({ id: quote.customer.id, label: customerLabel(quote.customer), type: quote.customer.type });
    setAddressId(quote.address ? String(quote.address.id) : '');
    setCustomerAddresses(await fetchDeliveryAddresses(quote.customer.id, quote.address?.id));
    setPoReference(quote.po_reference || '');
    setCustomerComment(quote.customer_comment || '');
    setStaffComment(quote.staff_comment || '');
    setCarrierId(quote.carrier_id != null ? String(quote.carrier_id) : '');
    setShippingServiceId(quote.shipping_service ? String(quote.shipping_service.id) : '');
    setShippingCost(quote.shipping_cost !== null && quote.shipping_cost !== undefined ? Number(quote.shipping_cost) : null);
    setShippingOverrideReason(quote.shipping_override_reason || '');
    discount.load(quote);
    setSelectedOptions((quote.selected_options || []).map((o) => ({
      shipping_option_id: o.shipping_option.id,
      cost: Number(o.cost),
      overridden: false,
    })));
    setEstimatePackagingId(quote.estimate_packaging_id != null ? String(quote.estimate_packaging_id) : '');
    setShippingEstimate(quote.shipping_estimate || null);
    setEstimateError('');
    setCustomerTax({
      tax_handling_type: quote.customer.tax_handling_type,
      payment_term: quote.customer.payment_term,
    });

    const loadedLines = await Promise.all((quote.lines || []).map(async (line) => {
      // Base UoM (e.g. Each) is frequently tracking-only, not sellable —
      // filtering to can_sell for the dropdown would also filter it out of
      // the base-UoM lookup used for the "= N Each" note, so the full,
      // unfiltered set is kept alongside the sellable-only dropdown options.
      let uomOptions = [];
      let allUomOptions = [];
      try {
        const uomRes = await axios.get(`/api/products/${line.product.id}/uoms`);
        allUomOptions = uomRes.data.data?.uoms || [];
        uomOptions = allUomOptions.filter((pu) => pu.can_sell);
      } catch (err) {
        uomOptions = [];
        allUomOptions = [];
      }
      return {
        key: nextRowKey(),
        productId: String(line.product.id),
        product: line.product,
        uomOptions,
        allUomOptions,
        uomId: String(line.uom.id),
        qty: String(line.qty),
        unitPrice: line.unit_price,
        unitPriceInc: line.unit_price_inc,
        extendedPrice: line.extended_price,
        referencePrice: line.reference_price,
        gstAmount: line.gst_amount,
        gstRate: line.gst_rate,
        extendedPriceIncGst: line.extended_price_inc_gst,
        overridden: line.overridden,
        overrideReason: line.override_reason || '',
        retailFallback: !!line.retail_fallback,
      };
    }));
    setLines(loadedLines);
    setSnapshotDue(true);
    return quote;
  };

  // Copy (vis_quotes.md): a new, unsaved Quote pre-filled from an existing
  // one — same Customer, delivery, Shipping Service, Options, comments and
  // lines — with every line priced fresh once the page is up (repriceDue),
  // which in turn re-costs the Options. Overrides, PO Reference and the
  // Shipping charge belonged to the old offer and aren't carried over.
  const copyFromId = !id ? location.state?.copyFromId : null;
  // Set when Copy's dialog chose to Withdraw the original ({ note }): sent
  // with the Save, which withdraws it in the same step.
  const withdrawOriginal = !id ? location.state?.withdrawOriginal || null : null;
  const [copySource, setCopySource] = useState(null);
  const [repriceDue, setRepriceDue] = useState(false);
  useEffect(() => {
    if (!copyFromId) return;
    setLoading(true);
    (async () => {
      const quote = (await axios.get(`/api/quotes/${copyFromId}`)).data.data;
      setCopySource(quote.display_number);
      setCustomerId(String(quote.customer.id));
      setCustomer({ id: quote.customer.id, label: customerLabel(quote.customer), type: quote.customer.type });
      setAddressId(quote.address ? String(quote.address.id) : '');
      setCustomerAddresses(await fetchDeliveryAddresses(quote.customer.id, quote.address?.id));
      setCustomerComment(quote.customer_comment || '');
      setStaffComment(quote.staff_comment || '');
      setCarrierId(quote.carrier_id != null ? String(quote.carrier_id) : '');
      setShippingServiceId(quote.shipping_service ? String(quote.shipping_service.id) : '');
      // A Copy keeps its source's discount and reason.
      discount.load(quote);
      setSelectedOptions((quote.selected_options || []).map((o) => ({
        shipping_option_id: o.shipping_option.id,
        cost: Number(o.cost),
        overridden: false,
      })));
      setEstimatePackagingId(quote.estimate_packaging_id != null ? String(quote.estimate_packaging_id) : '');
      setCustomerTax({
        tax_handling_type: quote.customer.tax_handling_type,
        payment_term: quote.customer.payment_term,
      });
      setLines(await Promise.all((quote.lines || []).map(async (line) => {
        let allUomOptions = [];
        try {
          const uomRes = await axios.get(`/api/products/${line.product.id}/uoms`);
          allUomOptions = uomRes.data.data?.uoms || [];
        } catch (err) {
          allUomOptions = [];
        }
        return {
          key: nextRowKey(),
          productId: String(line.product.id),
          product: line.product,
          uomOptions: allUomOptions.filter((pu) => pu.can_sell),
          allUomOptions,
          uomId: String(line.uom.id),
          qty: String(line.qty),
          unitPrice: null,
          unitPriceInc: null,
          extendedPrice: null,
          referencePrice: null,
          gstAmount: null,
          gstRate: null,
          extendedPriceIncGst: null,
          overridden: false,
          overrideReason: '',
        };
      })));
      setRepriceDue(true);
    })()
      .catch((err) => showError(err, 'Failed to copy Quote'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    axios.get('/api/shipping-services', { params: { status: 'all' } })
      .then((res) => setShippingServices(res.data.data || []))
      .catch(() => setShippingServices([]));
  }, []);

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then((res) => {
        setWeightUnit(res.data.data?.weight_unit || '');
        setEstimateWording(res.data.data?.shipping_estimate_wording || '');
        setPriceReminder(res.data.data?.quote_price_reminder || '');
        setValidDays(res.data.data?.quote_valid_days ?? null);
      })
      .catch(() => setWeightUnit(''));
  }, []);

  useEffect(() => {
    axios.get('/api/packagings', { params: { status: 'all' } })
      .then((res) => setPackagings(res.data.data || []))
      .catch(() => setPackagings([]));
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loadQuote()
      .then((quote) => {
        if (quote.refresh_error) {
          showError(undefined, `Pricing/GST could not be refreshed: ${quote.refresh_error}`);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const [refreshingQuote, setRefreshingQuote] = useState(false);
  const handleRefreshQuote = async () => {
    setRefreshingQuote(true);
    try {
      const quote = await loadQuote();
      if (quote.refresh_error) {
        showError(undefined, `Pricing/GST could not be refreshed: ${quote.refresh_error}`);
      } else {
        dismissError();
      }
    } catch (err) {
      showError(err, 'Failed to refresh Quote');
    }
    setRefreshingQuote(false);
  };

  // --- Customer picker ---

  const searchCustomers = (term) => axios
    .get('/api/customers', { params: { scope: 'quotable', search: term, status: 'active', limit: 10 } })
    .then((res) => res.data.data || []);

  // Fills the Address picker for a newly picked Customer, preselecting its
  // address when it has exactly one of its own. The ref drops a slow reply
  // for a Customer that has since been changed again.
  const addressRequest = useRef(0);
  const loadAddressesFor = async (customerIdToLoad) => {
    const request = ++addressRequest.current;
    const addresses = await fetchDeliveryAddresses(customerIdToLoad).catch(() => []);
    if (request !== addressRequest.current) return;
    setCustomerAddresses(addresses);
    const own = addresses.filter((a) => !a.recipient);
    if (own.length === 1) setAddressId(String(own[0].id));
  };

  const applyCustomer = (c) => {
    setCustomerId(String(c.id));
    setCustomer({ id: c.id, label: customerLabel(c), type: c.type });
    setGstView(null);
    setCustomerAddresses([]);
    setAddressId('');
    loadAddressesFor(c.id);
    setCarrierId(c.carrier_id != null ? String(c.carrier_id) : '');
    setShippingServiceId(c.shipping_service_id != null ? String(c.shipping_service_id) : '');
    // Cascades the Customer's own default Option selections onto this new
    // Quote, costed the same way ticking each by hand would (see
    // ShippingOptionsPicker.js's own recompute effect) — a brand-new Quote
    // has no lines yet, so the value basis starts at 0 and self-corrects
    // once real lines are added. Freely overridable/removable here without
    // touching the Customer's own default (same rule shipping_service_id
    // above already follows).
    setSelectedOptions((c.default_options || []).map((o) => ({
      shipping_option_id: o.shipping_option.id,
      cost: computeDefaultCost(o.shipping_option, 0),
      overridden: false,
    })));
    setCustomerTax({
      tax_handling_type: c.tax_handling_type,
      payment_term: c.payment_term,
    });
  };

  // Preload from the Customer detail page's Quotes card "Create Quote"
  // kebab action, which hands the already-loaded CommercialEntity record
  // through router state (same withEffectiveCommercials shape /api/customers
  // returns, so applyCustomer needs no separate fetch) rather than a
  // round-trip lookup by id.
  useEffect(() => {
    if (id) return;
    const preloadCustomer = location.state?.customer;
    if (preloadCustomer) applyCustomer(preloadCustomer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectCustomer = async (c) => {
    if (lines.length > 0 && String(c.id) !== customerId) {
      if (!(await askConfirm('Changing the Customer clears all lines, since prices are Customer-specific. Continue?', { confirmLabel: 'Change Customer', destructive: true }))) return;
      setLines([]);
    }
    applyCustomer(c);
  };

  // --- Line pricing ---

  const fetchLinePreview = async (productId, uomId, qty) => {
    if (!customerId || !productId || !uomId || !qty || Number(qty) < 1) return null;
    try {
      const res = await axios.get('/api/price-lookup', {
        params: { product_id: productId, customer_id: customerId, uom_id: uomId, qty },
      });
      const row = res.data.data?.rows?.[0];
      return row ? {
        retailFallback: res.data.data.appliedCustomerPriceGroupId == null,
        unitPrice: row.unitPrice,
        unitPriceInc: row.unitPriceInc,
        referencePrice: row.referencePrice,
        gstRate: row.gstRate,
      } : null;
    } catch (err) {
      return null;
    }
  };

  const refreshLinePreview = async (key, productId, uomId, qty) => {
    const preview = await fetchLinePreview(productId, uomId, qty);
    setLines((prev) => prev.map((l) => (l.key === key
      ? {
        ...(preview
          ? repriceLineLocally({ ...l, qty, gstRate: preview.gstRate, referencePrice: preview.referencePrice }, { ex: preview.unitPrice, inc: preview.unitPriceInc })
          : { ...l, unitPrice: null, unitPriceInc: null, extendedPrice: null, referencePrice: null, gstAmount: null, gstRate: null, extendedPriceIncGst: null }),
        retailFallback: preview ? preview.retailFallback : false,
        // A fresh system lookup always supersedes a prior override —
        // covers both qty/UoM change and the explicit revert action below.
        overridden: false,
        overrideReason: '',
      }
      : l)));
  };

  // Recalculates a line's figures from a given price ({ ex } or { inc }) at
  // its current qty, in the Customer's Pricing Basis — the same formula
  // resolveQuoteLines uses server-side (shared/pricingBasis.js).
  const repriceLineLocally = (l, { ex = null, inc = null }, extra = {}) => {
    const rate = Number(l.gstRate ?? (customerTax?.tax_handling_type?.tax_code ? customerTax.tax_handling_type.tax_code.rate : 0)) || 0;
    const figures = priceLine({ unitPriceEx: ex, unitPriceInc: inc, qty: l.qty, gstRate: rate, basis: basisOf(customerTax?.tax_handling_type) });
    return { ...l, ...figures, gstRate: rate, ...extra };
  };

  // A salesperson-entered price for a genuine one-off concession (see
  // vis_quotes.md's Pricing a Quote Line), typed in the Customer's basis —
  // inc for Retail, ex for Business. Extended/GST are still derived from it
  // locally the same way resolveQuoteLines derives them server-side, so the
  // on-screen totals stay in sync until Save re-resolves for real.
  const handleOverridePrice = (key, value) => {
    const price = Number(value);
    if (!Number.isFinite(price) || price < 0) return;
    const typed = basisOf(customerTax?.tax_handling_type).retail ? { inc: price } : { ex: price };
    setLines((prev) => prev.map((l) => (l.key === key ? repriceLineLocally(l, typed, { overridden: true }) : l)));
  };

  const handleOverrideReasonChange = (key, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, overrideReason: value } : l)));
  };

  useEffect(() => {
    if (!repriceDue) return;
    setRepriceDue(false);
    lines.forEach((l) => refreshLinePreview(l.key, l.productId, l.uomId, l.qty));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repriceDue]);

  const handleRevertOverride = (key) => {
    const line = lines.find((l) => l.key === key);
    if (line) refreshLinePreview(key, line.productId, line.uomId, line.qty);
  };

  // --- Draft (bottom, always-blank) row ---

  const handleDraftSearchChange = (value) => {
    setDraftRow((prev) => ({ ...prev, productSearch: value, productId: '', product: null }));
    clearTimeout(draftSearchTimer.current);
    if (!value.trim()) {
      setDraftRow((prev) => ({ ...prev, results: [], showResults: false }));
      return;
    }
    draftSearchTimer.current = setTimeout(() => {
      axios.get('/api/products', { params: { search: value, status: 'active', limit: 10 } })
        .then((res) => setDraftRow((prev) => ({ ...prev, results: res.data.data || [], showResults: true })))
        .catch(() => setDraftRow((prev) => ({ ...prev, results: [] })));
    }, 250);
  };

  const handleDraftSelectProduct = async (product) => {
    let uomOptions = [];
    let allUomOptions = [];
    try {
      const res = await axios.get(`/api/products/${product.id}/uoms`);
      allUomOptions = res.data.data?.uoms || [];
      uomOptions = allUomOptions.filter((pu) => pu.can_sell);
    } catch (err) {
      uomOptions = [];
      allUomOptions = [];
    }
    const firstUom = uomOptions[0];
    const key = nextRowKey();
    const newLine = {
      key,
      productId: String(product.id),
      product,
      uomOptions,
      allUomOptions,
      uomId: firstUom ? String(firstUom.uom_id) : '',
      qty: firstUom ? String(firstUom.min_order_qty) : '1',
      unitPrice: null,
      unitPriceInc: null,
      extendedPrice: null,
      referencePrice: null,
      gstAmount: null,
      gstRate: null,
      extendedPriceIncGst: null,
      overridden: false,
      overrideReason: '',
    };
    setLines((prev) => [...prev, newLine]);
    setDraftRow(blankDraftRow());
    if (firstUom) refreshLinePreview(key, product.id, firstUom.uom_id, newLine.qty);
  };

  // --- Committed line edits ---

  const handleLineUomChange = (key, uomId) => {
    setLines((prev) => prev.map((l) => {
      if (l.key !== key) return l;
      const uom = l.uomOptions.find((pu) => String(pu.uom_id) === String(uomId));
      return { ...l, uomId, qty: uom ? String(uom.min_order_qty) : l.qty };
    }));
    const line = lines.find((l) => l.key === key);
    const uom = line?.uomOptions.find((pu) => String(pu.uom_id) === String(uomId));
    if (line) refreshLinePreview(key, line.productId, uomId, uom ? String(uom.min_order_qty) : line.qty);
  };

  const handleLineQtyChange = (key, value) => {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, qty: value } : l)));
  };

  const handleLineQtyBlur = (key) => {
    const line = lines.find((l) => l.key === key);
    if (line) refreshLinePreview(key, line.productId, line.uomId, line.qty);
  };

  const handleRemoveLine = (key) => {
    setLines((prev) => prev.filter((l) => l.key !== key));
  };

  // --- Save / Cancel ---

  // A Quote's Firm Price promise means nothing about it is meant to change
  // again once it leaves Draft (vis_quotes.md) — a new Quote (no id yet)
  // is always editable, an existing one only while still Draft. Every
  // field below disables off this single flag rather than each guessing
  // status individually, so there's one place that decides "can this
  // Quote still be touched at all."
  // Also read-only without Create (new) or Update (existing) on Quotes.
  const isEditable = (id ? canUpdate : canCreate) && (!id || quoteMeta?.status === 'draft');
  // Lines are saved in the order they were added or dragged into — the
  // customer's order (issue #304). The Sequence switch only changes how
  // they're shown; dragging is off while they're shown in SKU order.
  const [lineOrder, setLineOrder] = useState('document');
  const shownLines = lineOrder === 'sku' ? bySku(lines) : lines;
  const { rowProps, handle } = useLineReorder(setLines, isEditable && lineOrder === 'document');
  const READONLY_MESSAGES = {
    sent: 'This Quote was Sent and can no longer be edited.',
    accepted: 'This Quote can no longer be edited as it has been accepted by the customer.',
    rejected: 'This Quote was Rejected and can no longer be edited.',
    withdrawn: 'This Quote was Withdrawn and can no longer be edited.',
    expired: 'This Quote has Expired and can no longer be edited.',
  };

  // Quote Date is an exception to isEditable above — it stays overridable
  // through Sent (unlike every other field, locked at Draft-only) and only
  // locks once the Quote reaches a terminal status (issue #213).
  const isQuoteDateEditable = !!id && ['draft', 'sent'].includes(quoteMeta?.status);
  const QUOTE_DATE_LOCKED_MESSAGES = {
    accepted: 'This Quote can no longer have its date changed — it has been accepted by the customer.',
    rejected: 'This Quote can no longer have its date changed — it was Rejected.',
    withdrawn: 'This Quote can no longer have its date changed — it was Withdrawn.',
    expired: 'This Quote can no longer have its date changed — it has Expired.',
  };
  const [savingQuoteDate, setSavingQuoteDate] = useState(false);
  // Bumped after a recorded change, remounting Status History so the step
  // just recorded shows.
  const [historyVersion, setHistoryVersion] = useState(0);
  const handleQuoteDateChange = async (e) => {
    const newDate = e.target.value;
    if (!newDate) return;
    const previous = quoteMeta.quoteDate;
    setQuoteMeta((prev) => ({ ...prev, quoteDate: newDate }));
    setSavingQuoteDate(true);
    try {
      await axios.patch(`/api/quotes/${id}/date`, { quote_date: newDate });
      dismissError();
      setHistoryVersion((v) => v + 1);
    } catch (err) {
      setQuoteMeta((prev) => ({ ...prev, quoteDate: previous }));
      showError(err, 'Failed to update Quote Date');
    }
    setSavingQuoteDate(false);
  };

  const subtotal = lines.reduce((sum, l) => sum + (l.extendedPrice ? Number(l.extendedPrice) : 0), 0);
  const anyNeedsPrice = lines.some((l) => l.unitPrice === null || l.unitPrice === undefined);

  // Total Weight — sums qty × the line's sold Product/UoM Weight (see
  // ProductWeightsPage.js; weight lives per-UoM, not per-Product). Read
  // straight off allUomOptions, already fetched per line for the UoM
  // dropdown — no extra request needed. A line whose Product/UoM has no
  // Weight set is excluded from the sum (not treated as 0) and flags
  // anyMissingWeight so the total reads as a lower bound, not a false total.
  const lineWeight = (l) => {
    const uom = (l.allUomOptions || l.uomOptions || []).find((pu) => String(pu.uom_id) === String(l.uomId));
    return uom && uom.weight !== null && uom.weight !== undefined ? Number(uom.weight) : null;
  };
  const totalWeight = lines.reduce((sum, l) => {
    const w = lineWeight(l);
    return w === null ? sum : sum + w * (Number(l.qty) || 0);
  }, 0);
  const anyMissingWeight = lines.some((l) => lineWeight(l) === null);
  const formatWeight = (n) => `${formatNumber(Number(n || 0), { maxDecimals: 2 })} ${weightUnit || 'kg'}`;
  // Shipping Service carries its own optional Disclaimer (issue #189) —
  // printed/shown alongside the Quote's own pricing note whenever the
  // selected one has text, not a generic hardcoded sentence.
  const selectedShippingService = shippingServices.find((sm) => String(sm.id) === String(shippingServiceId));
  // A Disclaimer can be kept on file without actively printing (its own
  // show_disclaimer toggle — see ShippingServiceForm.js).
  const showShippingDisclaimer = !!(selectedShippingService?.disclaimer && selectedShippingService?.show_disclaimer);
  // "Cost confirmed later" (vis_shipping_and_insurance.md's "Cost Not Yet
  // Known") — while Shipping Cost is genuinely still blank on a Service
  // marked this way, show the admin's own wording instead of a dollar
  // figure; entering a real figure makes it disappear (matches
  // QuoteReportView.js's own showPendingCost).
  const shippingCostBlank = shippingCost === null || shippingCost === undefined;
  const showPendingCost = shippingCostBlank && !!selectedShippingService?.cost_confirmed_later;
  const showPendingCostDisclaimer = showPendingCost && !!selectedShippingService?.pending_cost_disclaimer;

  // Estimate Shipping (issue #327) — only for an Australia Post Service.
  const canEstimate = isAustraliaPost(selectedShippingService?.supplier?.name);
  const estimate = canEstimate ? shippingEstimate : null;
  // Estimate Shipping's wording runs on after footnote 1 when the Service
  // has a disclaimer too (joinShippingNotes), else it's its own * note.
  const showEstimateWording = !!(estimate && !shippingCostBlank && estimateWording);
  const currentInputsKey = estimateInputsKey({ addressId, shippingServiceId, packagingId: estimatePackagingId, selectedOptions, lines });
  const typedOver = chargeTypedOver(estimate, shippingCost);

  // Same cascade and same display rules QuoteReportView.js (Print) uses —
  // the edit screen's totals should never show something Print wouldn't.
  const taxHandling = customerTax?.tax_handling_type;
  const gstApplicable = !!taxHandling?.tax_code && Number(taxHandling.tax_code.rate) > 0;
  // Every price is in the Customer's Pricing Basis — inc GST for Retail,
  // ex GST for Business — one figure per row.
  const basis = basisOf(taxHandling);
  // The Ex GST / Inc GST switch (shared/gstView.js) starts on that basis
  // and can show staff the other side — on screen only (Print stays in
  // the Customer's basis), and Unit Price turns read-only while it's off it.
  const viewInc = gstApplicable && (gstView ? gstView === 'inc' : basis.retail);
  const offBasis = gstApplicable && viewInc !== basis.retail;
  const basisSuffix = gstApplicable ? (viewInc ? ' (inc GST)' : ' (ex GST)') : '';
  // The totals follow the switch too: Subtotal, Shipping and Options Cost
  // all ex GST (then GST, then the Total) or all inc GST (adding up to the
  // Total, which includes the GST), so they add up down the column.
  const basisLine = <TaxBasisLine>{basisSuffix.slice(2, -1)}</TaxBasisLine>;
  const lineGstRate = (line) => line.gstRate ?? taxHandling?.tax_code?.rate;
  const linePrice = (line) => (offBasis ? viewUnitPrice(line, lineGstRate(line), viewInc) : shownUnitPrice(line, lineGstRate(line), basis));
  const lineAmount = (line) => (offBasis ? viewLineAmount(line, viewInc) : shownLineAmount(line, basis));
  // A pack line's price per base unit (e.g. per PCS), shown under Unit
  // Price to match the base qty under UoM — same rule as SalesOrderPage.js.
  // Up to 4 decimals so a pack price split across its units isn't rounded away.
  const baseUnitPriceLabel = (line) => {
    if (linePrice(line) === null) return null;
    const selectedUom = line.uomOptions.find((pu) => String(pu.uom_id) === String(line.uomId));
    const baseUom = (line.allUomOptions || line.uomOptions).find((pu) => pu.is_base);
    const rate = Number(selectedUom?.conversion_rate);
    if (!selectedUom || !baseUom || selectedUom.is_base || !(rate > 0)) return null;
    const price = linePrice(line) / rate;
    const decimals = Number(price.toFixed(2)) === Number(price.toFixed(4)) ? 2 : 4;
    return `${formatMoney(price, { decimals })} / ${baseUom.code || baseUom.name}`;
  };

  // Shipping Cost (issue #196) and Options Cost (issue #193, the sum of
  // selectedOptions) fold into the same single GST figure, at the
  // Customer's one standard rate (see vis_quotes.md). GST is rounded once
  // for the whole document (shared/pricingBasis.js).
  const gstRate = Number(taxHandling?.tax_code?.rate) || 0;
  const shippingCostEx = Number(shippingCost || 0);
  const optionsCostEx = selectedOptions.reduce((sum, o) => sum + (Number(o.cost) || 0), 0);
  const totals = documentTotals({ lines, shippingEx: shippingCostEx, optionsEx: optionsCostEx, gstRate: gstApplicable ? gstRate : 0, basis, discount: discount.forTotals });

  const buildPayload = () => ({
    customer_id: customerId,
    address_id: addressId || null,
    po_reference: poReference || null,
    customer_comment: customerComment.trim() || null,
    staff_comment: staffComment.trim() || null,
    carrier_id: carrierId || null,
    shipping_service_id: shippingServiceId || null,
    shipping_cost: shippingCost,
    options: selectedOptions.map((o) => ({ shipping_option_id: o.shipping_option_id, cost: o.cost })),
    estimate_packaging_id: canEstimate ? (estimatePackagingId || null) : null,
    shipping_estimate: estimate,
    shipping_override_reason: typedOver ? (shippingOverrideReason.trim() || null) : null,
    ...discount.payload,
    lines: lines.map((l) => ({
      product_id: l.productId,
      uom_id: l.uomId,
      qty: Number(l.qty),
      overridden: !!l.overridden,
      // An override goes back in the basis it was typed in.
      unit_price: l.overridden && !basis.retail ? Number(l.unitPrice) : undefined,
      unit_price_inc: l.overridden && basis.retail ? Number(l.unitPriceInc) : undefined,
      override_reason: l.overridden ? (l.overrideReason || null) : undefined,
    })),
  });

  // Prices the Quote as it is on screen in the given Packaging — what
  // Estimate Shipping, and a re-estimate at Save, send.
  const requestEstimate = async (packagingId) => {
    const { lines: payloadLines, options, customer_id: payloadCustomer, address_id: payloadAddress, shipping_service_id: payloadService } = buildPayload();
    const res = await axios.post('/api/quotes/shipping-estimate', {
      customer_id: payloadCustomer,
      address_id: payloadAddress,
      shipping_service_id: payloadService,
      packaging_id: packagingId || null,
      options,
      lines: payloadLines,
      inputs_key: estimateInputsKey({ addressId, shippingServiceId, packagingId, selectedOptions, lines }),
      // Shipping Charge Rounding lands on the figure the Customer sees.
      gst_rate: gstApplicable ? gstRate : 0,
    });
    return res.data.data;
  };
  const showEstimate = (next, packagingId) => {
    setEstimatePackagingId(packagingId || '');
    setShippingEstimate(next);
    setShippingCost(Number(next.charge));
    setShippingOverrideReason('');
  };

  // Re-estimate Shipping on Save (issue #437).
  const { confirmReestimate, reestimateDialog, settle: settleInputsKey } = useReestimateOnSave({
    estimate, currentInputsKey, shippingCost, typedOver, locked: false,
    packagings, packagingId: estimatePackagingId, requestEstimate, onReestimated: showEstimate,
    gstApplicable, documentName: 'Quote',
  });

  // For the Open customer link's "Leave without saving?": what Save would
  // send, as last loaded, compared with what it would send now.
  const [savedPayload, setSavedPayload] = useState(null);
  const [snapshotDue, setSnapshotDue] = useState(false);
  useEffect(() => {
    if (!snapshotDue) return;
    setSavedPayload(JSON.stringify(buildPayload()));
    settleInputsKey(currentInputsKey);
    setSnapshotDue(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotDue]);
  const hasUnsavedChanges = () => isEditable && (!id || JSON.stringify(buildPayload()) !== savedPayload);

  // Save stays on the Quote (a new one moves onto its own page) so a long
  // Quote can be saved as it's built; Save & Close (close true) goes back
  // to the list. saveMode says which is running, for its spinner. Same
  // pair as SalesOrderPage.js.
  const saveKey = useSaveKey();
  const [saveMode, setSaveMode] = useState(null);
  const handleSave = async (close) => {
    if (!customerId || !isEditable) return;
    const discountProblem = discount.problem(totals.linesAmount);
    if (discountProblem) {
      showError(undefined, discountProblem);
      return;
    }
    const reestimated = await confirmReestimate();
    if (!reestimated) return;
    setSaving(true);
    setSaveMode(close ? 'close' : 'stay');
    try {
      const payload = { ...buildPayload(), ...reestimated };
      if (id) {
        await axios.put(`/api/quotes/${id}`, payload);
      } else {
        const res = await axios.post('/api/quotes', {
          ...payload,
          save_key: saveKey,
          copied_from_quote_id: copyFromId || undefined,
          withdraw_original: withdrawOriginal || undefined,
        });
        // The original moved on (Accepted, Expired...) while this copy was
        // open, so it couldn't be withdrawn — say so before leaving.
        if (withdrawOriginal && !/withdrawn$/.test(res.data.message || '')) await showMessage(res.data.message, { title: 'Original Quote Not Withdrawn' });
        if (!close) {
          dismissError();
          navigate(`/quotes/${res.data.data.id}`, { replace: true });
          return;
        }
      }
      dismissError();
      if (close) {
        navigate('/sales-quotes');
        return;
      }
      await loadQuote();
      setHistoryVersion((v) => v + 1);
    } catch (err) {
      showError(err, 'Failed to save Quote');
    } finally {
      setSaving(false);
      setSaveMode(null);
    }
  };

  // Cancel leaves without saving, checking first if anything's changed.
  const handleCancel = async () => {
    const unsaved = id ? hasUnsavedChanges() : isEditable && (!!customerId || lines.length > 0);
    if (unsaved && !(await askConfirm('Leave without saving? Your changes will be lost.', { confirmLabel: 'Leave', destructive: true }))) return;
    navigate('/sales-quotes');
  };

  // Estimate Shipping (issue #327): prices the Quote as it is on screen and
  // fills the Shipping charge with the postage. A charge staff typed over
  // is only replaced if they say so.
  const handleEstimate = async () => {
    if (typedOver && !(await askConfirm('The Shipping charge was typed in. Re-estimating replaces it with the new estimate. Continue?', { confirmLabel: 'Replace Charge' }))) return;
    setEstimating(true);
    setEstimateError('');
    try {
      showEstimate(await requestEstimate(estimatePackagingId), estimatePackagingId);
    } catch (err) {
      setEstimateError(err.response?.data?.message || 'Couldn’t estimate shipping');
    }
    setEstimating(false);
  };

  // Selecting another Australia Post method's price (issue #328): the
  // estimate shows that method's price and the Shipping charge becomes it.
  // A charge staff typed over is only replaced if they say so.
  const handleSelectMethod = async (key) => {
    const method = (shippingEstimate?.methods || []).find((m) => m.key === key && m.ok);
    if (!method || shippingEstimate.selected_method === key) return;
    if (typedOver && !(await askConfirm(`The Shipping charge was typed in. Selecting ${method.name} replaces it with its price. Continue?`, { confirmLabel: 'Replace Charge' }))) return;
    setShippingEstimate({
      ...shippingEstimate,
      selected_method: key,
      postage: method.postage,
      options: method.options,
      options_cost: method.options_cost,
      rounding: method.rounding,
      total: method.total,
      charge: method.charge,
      note: method.note,
    });
    setShippingCost(Number(method.charge));
    setShippingOverrideReason('');
  };

  // Shared by every lifecycle-transition endpoint's response (Send, Accept,
  // Reject, Withdraw) — each returns the same full Quote shape withDisplayNumber
  // already produces, so quoteMeta is rebuilt the same way regardless of
  // which transition just happened.
  const quoteMetaFromData = (data) => ({
    display_number: data.display_number,
    status: data.status,
    createdAt: data.createdAt,
    quoteDate: data.quote_date,
    validUntil: data.valid_until,
    sentAt: data.sent_at,
    acceptedAt: data.accepted_at,
    withdrawnAt: data.withdrawn_at,
    rejectedAt: data.rejected_at,
    expiredAt: data.expired_at,
    convertedSalesOrder: data.converted_sales_order,
    copiedFrom: data.copied_from,
    originLabel: data.origin_label,
    createdByName: data.created_by_name,
  });

  const [sending, setSending] = useState(false);
  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.patch(`/api/quotes/${id}/send`);
      setQuoteMeta(quoteMetaFromData(res.data.data));
      dismissError();
    } catch (err) {
      showError(err, 'Failed to send Quote');
    }
    setSending(false);
  };

  // After Email Quote sends — a Draft is now Sent, so re-read its status.
  const handleEmailed = async () => {
    try {
      const res = await axios.get(`/api/quotes/${id}`);
      setQuoteMeta(quoteMetaFromData(res.data.data));
      dismissError();
    } catch (err) {
      showError(err, 'Quote emailed, but it failed to reload');
    }
  };

  // Payments (issue #338): recording one on a Sent Quote Accepts it, so the
  // status and Status History are re-read after each change.
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const handlePaymentsChanged = async () => {
    setHistoryVersion((v) => v + 1);
    try {
      const res = await axios.get(`/api/quotes/${id}`);
      setQuoteMeta(quoteMetaFromData(res.data.data));
    } catch (err) {
      showError(err, 'Payment saved, but the Quote failed to reload');
    }
  };

  const [accepting, setAccepting] = useState(false);
  const handleAccept = async () => {
    setAccepting(true);
    try {
      const res = await axios.patch(`/api/quotes/${id}/accept`);
      setQuoteMeta(quoteMetaFromData(res.data.data));
      dismissError();
    } catch (err) {
      showError(err, 'Failed to mark Quote Accepted');
    }
    setAccepting(false);
  };

  // Withdraw and Reject share one dialog: both close the Quote for good,
  // with a date (defaulting to today, never in the future) and an optional
  // note.
  const CLOSE_ACTIONS = {
    withdraw: {
      endpoint: 'withdraw', dateField: 'withdrawn_date', title: 'Withdraw Quote', statusName: 'Withdrawn',
      dateLabel: 'Withdrawal Date', confirm: 'Confirm Withdraw', busy: 'Withdrawing...', failed: 'Failed to withdraw Quote',
    },
    reject: {
      endpoint: 'reject', dateField: 'rejected_date', title: 'Reject Quote', statusName: 'Rejected',
      dateLabel: 'Rejection Date', confirm: 'Confirm Reject', busy: 'Rejecting...', failed: 'Failed to mark Quote Rejected',
    },
  };
  const [closeAction, setCloseAction] = useState(null);
  const [closeNote, setCloseNote] = useState('');
  const [closeDate, setCloseDate] = useState('');
  const [closing, setClosing] = useState(false);
  const todayISO = todayInTenant();
  const openCloseDialog = (key) => { setCloseDate(todayISO); setCloseAction(key); };
  const resetCloseDialog = () => { setCloseAction(null); setCloseNote(''); setCloseDate(''); };
  const handleConfirmClose = async () => {
    const action = CLOSE_ACTIONS[closeAction];
    setClosing(true);
    try {
      const res = await axios.patch(`/api/quotes/${id}/${action.endpoint}`, {
        note: closeNote.trim() || undefined,
        [action.dateField]: closeDate,
      });
      setQuoteMeta(quoteMetaFromData(res.data.data));
      resetCloseDialog();
      dismissError();
    } catch (err) {
      showError(err, action.failed);
    }
    setClosing(false);
  };

  const [converting, setConverting] = useState(false);
  // Shown only when the Quote has a comment to offer carrying forward;
  // each comment's tickbox starts ticked.
  const [convertDialog, setConvertDialog] = useState(null);
  const convertQuote = async (copy) => {
    setConverting(true);
    try {
      const res = await axios.post(`/api/quotes/${id}/convert-to-sales-order`, copy);
      dismissError();
      navigate(`/sales-orders/${res.data.data.id}`, { state: { justConverted: true, acceptMissing: res.data.accept_missing, acceptUnlinked: res.data.accept_unlinked } });
    } catch (err) {
      showError(err, 'Failed to convert Quote to Sales Order');
      setConverting(false);
    }
  };
  const handleConvert = async () => {
    // Convert Accepts the new order, so no shipping charge warns first
    // (issue #351).
    if (shippingNotAdded(selectedShippingService, shippingCost)
      && !(await askConfirm('Shipping hasn’t been added to this Quote. Convert to a Sales Order anyway?', { confirmLabel: 'Convert Anyway' }))) return;
    if (customerComment.trim() || staffComment.trim()) {
      setConvertDialog({ copy_customer_comment: true, copy_staff_comment: true });
      return;
    }
    if (!(await askConfirm(`Convert ${quoteMeta.display_number} to a Sales Order?`, { confirmLabel: 'Convert' }))) return;
    convertQuote({ copy_customer_comment: false, copy_staff_comment: false });
  };

  // Copy offers to Withdraw the original while it still can be (Draft or
  // Sent), since a Sent Quote never changes: a changed offer is a new Quote.
  const [copyDialog, setCopyDialog] = useState(null);
  const startCopy = (withdraw) => navigate('/quotes/new', { state: { copyFromId: id, withdrawOriginal: withdraw } });
  const handleCopy = () => {
    if (['draft', 'sent'].includes(quoteMeta.status)) setCopyDialog({ withdraw: false, note: '' });
    else startCopy(null);
  };

  const emailQuote = () => setShowEmail(true);
  // The header's Next button: the one step that moves the Quote on from
  // its status — Email Quote a Draft, Accept a Sent one, Convert an
  // Accepted one. The Actions menu keeps the rest.
  let nextStep = null;
  if (id && canUpdate && quoteMeta?.status === 'draft') {
    nextStep = { label: 'Email Quote', onClick: emailQuote };
  } else if (id && canUpdate && quoteMeta?.status === 'sent') {
    nextStep = { label: accepting ? 'Accepting...' : 'Accept', onClick: handleAccept, disabled: accepting };
  } else if (id && quoteMeta?.status === 'accepted' && !quoteMeta?.convertedSalesOrder && accessFor('sales-orders').create) {
    nextStep = { label: converting ? 'Converting...' : 'Convert to SO', onClick: handleConvert, disabled: converting };
  }

  // Grouped per ui-architecture.md's Actions Menu Grouping: the workflow
  // steps open at this status other than the Next button's, then the
  // standing actions.
  const actionsMenuItems = [];
  // Email Quote (issue #332) — emails the Quote PDF to the Customer, and
  // sends a Draft (the Next button there). Mark as Sent stays for a Quote
  // handed over another way.
  if (id && quoteMeta?.status === 'sent') {
    actionsMenuItems.push({
      key: 'email-quote',
      needs: 'update',
      label: 'Email Quote',
      onSelect: emailQuote,
    });
  }
  if (id && quoteMeta?.status === 'draft') {
    actionsMenuItems.push({
      key: 'send-quote',
      needs: 'update',
      label: sending ? 'Marking as Sent...' : 'Mark as Sent',
      onSelect: handleSend,
      disabled: sending,
    });
  }
  if (id && quoteMeta?.status === 'sent') {
    actionsMenuItems.push({
      key: 'reject-quote',
      needs: 'update',
      label: 'Reject',
      onSelect: () => openCloseDialog('reject'),
    });
  }
  if (id && ['draft', 'sent'].includes(quoteMeta?.status)) {
    actionsMenuItems.push({
      key: 'withdraw-quote',
      needs: 'update',
      label: 'Withdraw',
      onSelect: () => openCloseDialog('withdraw'),
    });
  }
  // A Prepay Customer's payment (issue #338) — on a Sent Quote it Accepts
  // the Quote; once converted, payments go on the Sales Order.
  if (id && paymentSummary?.can_record) {
    actionsMenuItems.push({
      key: 'record-payment',
      needs: 'update',
      label: 'Record Payment',
      onSelect: () => setRecordPaymentOpen(true),
    });
  }
  actionsMenuItems.push({ key: 'divider-standing', divider: true });
  actionsMenuItems.push({ key: 'print', label: 'Print Quote', onSelect: () => setShowReport(true), disabled: !id, disabledTitle: !id ? 'Save the Quote first' : undefined });
  if (id && quoteMeta?.status === 'draft') {
    actionsMenuItems.push({
      key: 'refresh-quote',
      needs: 'update',
      label: refreshingQuote ? 'Refreshing...' : 'Refresh Quote',
      onSelect: handleRefreshQuote,
      disabled: refreshingQuote,
    });
  }
  if (id && quoteMeta) {
    actionsMenuItems.push({
      key: 'copy-quote',
      needs: 'create',
      label: 'Copy',
      onSelect: handleCopy,
    });
  }
  // Who changed this Quote and what changed (issue #391).
  if (id && quoteMeta && accessFor('global-audit').read) {
    actionsMenuItems.push({
      key: 'audit-log',
      label: 'Audit Log',
      onSelect: () => navigate(`/global-audit?${new URLSearchParams({ document_type: 'quote', document_id: id, number: quoteMeta.display_number || '' })}`),
    });
  }

  if (loading) {
    return <div style={{ width: '100%', maxWidth: 1000, margin: '32px auto', textAlign: 'center', color: '#888' }}>Loading...</div>;
  }
  if (notFound) {
    return <div style={{ width: '100%', maxWidth: 1000, margin: '32px auto', textAlign: 'center', color: '#888' }}>Quote not found</div>;
  }

  return (
    <div className="detail-timeline-row" style={{ width: '100%', maxWidth: 1450, margin: '32px auto 48px' }}>
    <div className="detail-timeline-main" style={{ background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <Accordion
        title={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, fontSize: 18, color: '#1a1a1a' }}>
            {quoteMeta ? `Quote ${quoteMeta.display_number}` : 'New Quote'}
            {quoteMeta && customer?.label && <span style={{ fontWeight: 400, fontSize: 15, color: '#555' }}>· {customer.label}</span>}
            {quoteMeta?.quoteDate && <span style={{ fontWeight: 400, fontSize: 15, color: '#555' }}>· {formatDate(quoteMeta.quoteDate)}</span>}
            {quoteMeta && (
              <CategoryPill
                value={quoteMeta.convertedSalesOrder ? 'Converted' : (STATUS_LABELS[quoteMeta.status] || quoteMeta.status)}
                color={quoteMeta.convertedSalesOrder ? CONVERTED_COLOR : QUOTE_STATUS_COLORS[quoteMeta.status]}
              />
            )}
            {quoteMeta && <PaymentStatusPill status={paymentSummary?.payment_status} />}
          </span>
        )}
        // The Next button and the page's one Actions menu at the top of the
        // page, as on the Sales Order — in headerExtra, since the title is a
        // button.
        className="doc-header"
        headerExtra={(
          <>
            {nextStep && <NextStepButton {...nextStep} />}
            <ActionsMenu style={{ marginLeft: 8 }} items={actionsMenuItems} placement="down" />
          </>
        )}
        showMenuPlaceholder={false}
        // Open on a new document (Customer, delivery and Shipping come
        // first); collapsed on an existing one, so the lines lead.
        defaultOpen={!id}
      >
      {copySource && <DuplicateBanner label="Quote" />}
      {copySource && withdrawOriginal && (
        <div style={{ fontSize: 13, color: '#555', margin: '-8px 0 18px' }}>
          {copySource} will be withdrawn when this Quote is saved.
        </div>
      )}
      {/* Report-style metadata line — a flowing grey summary rather than
          the boxed field-per-value grid this used to be, matching how
          QuoteReportView.js/other ReportViews present their own metadata.
          Tax Display/GST Breakout are the Customer's own settings, silently
          driving how totals are formatted below — shown here so that's
          checkable while drafting instead of only inferable from the
          numbers. Shipping Cost/Options Cost live in the totals box below,
          alongside GST, rather than in this line. Payment Terms is pulled out into its own field
          below instead of living in this line — worth double-checking by eye
          before Save/Print rather than buried in a run-on sentence; may
          become directly editable/overridable later, read-only for now. */}
      <div style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
        {!quoteMeta && 'Not yet saved · '}
        Date{' '}
        {isQuoteDateEditable ? (
          <DateInput
            className="styled-input"
            value={quoteMeta.quoteDate || ''}
            onChange={handleQuoteDateChange}
            disabled={savingQuoteDate}
            style={{ width: 130, padding: '2px 6px', fontSize: 13 }}
          />
        ) : (
          <span title={quoteMeta ? QUOTE_DATE_LOCKED_MESSAGES[quoteMeta.status] : undefined}>
            {quoteMeta ? formatDate(quoteMeta.quoteDate) : formatDate(todayInTenant())}
          </span>
        )}
        {' · '}Valid Until{' '}
        <span title="Locked in — from the tenant's Quotes-valid-for-days default — the moment this Quote is Sent. Not set on a Draft.">
          {quoteMeta && quoteMeta.status !== 'draft' ? (formatDate(quoteMeta.validUntil) || '—') : 'Set when Sent'}
        </span>
        {customerTax && (
          <>
            {' · '}Tax Handling {taxHandling?.name || '—'}
          </>
        )}
        {quoteMeta && (
          <>
            {' · '}Origin {quoteMeta.originLabel || '—'}
            {' · '}Created by {quoteMeta.createdByName || '—'}
          </>
        )}
      </div>

      {id && quoteMeta && !isEditable && (
        <div style={{ padding: '10px 16px', marginBottom: 20, background: '#eef4fb', border: '1px solid #cfe0f3', borderRadius: 8, fontSize: 13, color: '#1e3a5f' }}>
          {quoteMeta.status === 'draft' ? 'Your roles let you view this Quote but not change it.' : (READONLY_MESSAGES[quoteMeta.status] || 'This Quote can no longer be edited.')}
          {/* The lock reason is always the Quote's own status (Accepted, not
              Conversion — a Sales Order being raised from it is a follow-on
              operational step, not why editing stops) — Convert just adds a
              secondary pointer to where the Customer's order actually is. */}
          {quoteMeta.convertedSalesOrder && (
            <>
              {' '}It was converted to Sales Order{' '}
              <Link to={`/sales-orders/${quoteMeta.convertedSalesOrder.id}`} style={{ color: '#1976d2', textDecoration: 'underline' }}>
                {quoteMeta.convertedSalesOrder.display_number}
              </Link>.
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 300 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Customer<RequiredMark /></label>
          <RecordLookupField
            value={customer}
            search={searchCustomers}
            optionLabel={customerLabel}
            onSelect={handleSelectCustomer}
            href={customer ? `/commercial-entities/${customer.id}` : undefined}
            icon={customer?.type === 'individual' ? UserRound : Building2}
            readOnly={!isEditable}
            placeholder="Search code or name..."
            origin={{ label: quoteMeta ? quoteMeta.display_number : 'New Quote', path: id ? `/quotes/${id}` : '/quotes/new' }}
            noun="Quote"
            hasUnsavedChanges={hasUnsavedChanges}
            openTitle="Open customer"
            changeTitle="Change customer"
          />
          <div style={{ marginTop: 12 }}>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Purchase Order Reference</label>
            <input
              className="styled-input"
              placeholder="Customer's PO number"
              value={poReference}
              onChange={(e) => setPoReference(e.target.value)}
              disabled={!isEditable}
              style={{ fontSize: 14, width: 300 }}
            />
          </div>
        </div>

        <div style={{ minWidth: 260, width: 260 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Address</label>
          <AddressPicker
            addresses={customerAddresses}
            value={addressId}
            onChange={setAddressId}
            disabled={!isEditable || !customerId || customerAddresses.length === 0}
          />
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Payment Terms</label>
          <div style={{ padding: '6px 0', fontSize: 14 }}>
            {customerTax?.payment_term?.name || '—'}
          </div>
        </div>

        {/* Carrier and Shipping Service as one group, side by side, so
            they wrap together — as SalesOrderPage.js's Shipping card. */}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Carrier</label>
            <select
              className="styled-input"
              value={carrierId}
              onChange={(e) => { setCarrierId(e.target.value); setShippingServiceId(''); }}
              disabled={!isEditable}
              style={{ fontSize: 14, width: 200 }}
            >
              <option value="">(none)</option>
              {carrierChoices(shippingServices, carrierId).map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Shipping Service</label>
            <select
              className="styled-input"
              value={shippingServiceId}
              onChange={(e) => { setCarrierId(carrierForService(shippingServices, e.target.value, carrierId)); setShippingServiceId(e.target.value); }}
              disabled={!isEditable}
              style={{ fontSize: 14, width: 200 }}
            >
              <option value="">(none)</option>
              {servicesForCarrier(shippingServices, carrierId, shippingServiceId).map((sm) => (
                <option key={sm.id} value={sm.id}>{sm.description}</option>
              ))}
            </select>
            <ShippingOptionsPicker
              shippingServiceId={shippingServiceId}
              quoteSubtotalExGst={subtotal}
              lines={lines}
              gstApplicable={gstApplicable}
              gstRate={gstRate}
              locked={!isEditable}
              selectedOptions={selectedOptions}
              onChange={setSelectedOptions}
            />
          </div>
        </div>
      </div>
      {canEstimate && (
        <QuoteShippingEstimate
          packagings={packagings}
          packagingId={estimatePackagingId}
          onPackagingChange={setEstimatePackagingId}
          estimate={estimate}
          typedOver={typedOver}
          selectedOptions={selectedOptions}
          onEstimate={handleEstimate}
          onSelectMethod={handleSelectMethod}
          estimating={estimating}
          error={estimateError}
          locked={!isEditable}
          gstApplicable={gstApplicable}
          viewInc={viewInc}
          gstRate={gstRate}
        />
      )}
      </Accordion>

      {/* Fixed column widths from first render, not content-driven — without
          this the grid starts narrow (just header labels) and visibly jumps
          wider the moment the first Product's name/description gives the
          Product column something to size against. */}
      <div style={{ marginTop: 24 }}>
      <Accordion
        title={`Lines (${lines.length})`}
        showMenuPlaceholder={false}
        headerExtra={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
            {anyNeedsPrice && <span style={{ fontSize: 12, color: '#b45309' }}>One or more lines need a price</span>}
            {(gstApplicable || !basis.retail) && <span>Subtotal{basisSuffix} {formatPrice(viewSubtotal(lines, viewInc))}</span>}
            {gstApplicable && <SegmentedToggle options={GST_VIEWS} value={viewInc ? 'inc' : 'ex'} onChange={setGstView} />}
          </span>
        )}
      >
        {lines.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <LineOrderSwitch value={lineOrder} onChange={setLineOrder} />
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '43%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '7%' }} />
            <col style={{ width: '8%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '4%' }} />
          </colgroup>
          <thead style={{ fontSize: 13 }}>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid #b3bbc7' }}>
              <th style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>#</th>
              <th style={{ padding: '6px 8px 6px 0' }}>SKU</th>
              <th style={{ padding: '6px 8px 6px 0' }}>Product</th>
              <th style={{ padding: '6px 8px 6px 0' }}>UoM</th>
              <th style={{ padding: '6px 18px 6px 0', textAlign: 'right' }}>Qty</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Unit Price{basisLine}</th>
              <th style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>Amount{basisLine}</th>
              <th style={{ padding: '6px 8px 6px 0', width: 20 }} />
            </tr>
          </thead>
          <tbody>
            {shownLines.map((line) => (
              <tr key={line.key} className="list-row" {...rowProps(line, { verticalAlign: 'top' })}>
                <td style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>{handle(line)}{lines.indexOf(line) + 1}</td>
                <td style={{ padding: '6px 8px 6px 0' }}>
                  {line.product?.sku}
                  {lineWeight(line) === null && (
                    <span title="Missing Weight" style={{ display: 'inline-flex', cursor: 'help', marginLeft: 3, verticalAlign: -1 }}>
                      <Info size={13} strokeWidth={2.5} style={{ color: '#b45309' }} />
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0' }}>
                  {line.product?.name}
                  {(line.product?.availability_badge || line.product?.availability_date) && (
                    <>
                      {' '}
                      <AvailabilityPill
                        badge={line.product.availability_badge}
                        date={line.product.availability_date}
                        color={line.product.availability_badge_color}
                        showDate
                      />
                    </>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0' }}>
                  {isEditable ? (
                    <select
                      className="styled-input"
                      value={line.uomId}
                      onChange={(e) => handleLineUomChange(line.key, e.target.value)}
                      style={{ fontSize: 12, width: '100%', minWidth: 0 }}
                    >
                      {line.uomOptions.map((pu) => (
                        <option key={pu.uom_id} value={pu.uom_id}>{pu.name}</option>
                      ))}
                    </select>
                  ) : (
                    line.uomOptions.find((pu) => String(pu.uom_id) === String(line.uomId))?.name || '—'
                  )}
                  {(() => {
                    const selectedUom = line.uomOptions.find((pu) => String(pu.uom_id) === String(line.uomId));
                    const baseUom = (line.allUomOptions || line.uomOptions).find((pu) => pu.is_base);
                    if (!selectedUom || !baseUom || selectedUom.is_base) return null;
                    const baseQty = (Number(line.qty) || 0) * Number(selectedUom.conversion_rate || 1);
                    return (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'nowrap' }}>
                        {formatNumber(baseQty, { maxDecimals: 2 })} {baseUom.code || baseUom.name}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ padding: '6px 18px 6px 0', textAlign: 'right' }}>
                  {isEditable ? (
                    <NumberInput
                      min={1}
                      maxDecimals={4}
                      stepper
                      value={line.qty}
                      onChange={(e) => handleLineQtyChange(line.key, e.target.value)}
                      onBlur={() => handleLineQtyBlur(line.key)}
                      style={{ width: '100%', minWidth: 0, fontSize: 12, textAlign: 'right' }}
                    />
                  ) : formatNumber(line.qty, { maxDecimals: 4 })}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                  {isEditable && !offBasis ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, width: '100%' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                        <LineUnitPriceField price={linePrice(line)} onCommit={(val) => handleOverridePrice(line.key, val)} />
                        <PriceCalculator initialValue={linePrice(line)} onResult={(val) => handleOverridePrice(line.key, val)} />
                        {line.overridden && (
                          <OverrideRevertBadge
                            onClick={() => handleRevertOverride(line.key)}
                            title="Overridden — click to restore the system price"
                          />
                        )}
                      </div>
                      {baseUnitPriceLabel(line) && (
                        <div style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{baseUnitPriceLabel(line)}</div>
                      )}
                      {line.retailFallback && !line.overridden && <RetailFallbackPill name={anchorGroupName} />}
                      {line.overridden && (
                        <input
                          className="styled-input"
                          type="text"
                          placeholder="Reason (optional)"
                          value={line.overrideReason || ''}
                          onChange={(e) => handleOverrideReasonChange(line.key, e.target.value)}
                          style={{ width: 140, fontSize: 11, padding: '2px 6px', height: 22 }}
                        />
                      )}
                    </div>
                  ) : (
                    <div>
                      {formatUnitPrice(linePrice(line))}
                      {baseUnitPriceLabel(line) && (
                        <div style={{ fontSize: 10, color: '#888', whiteSpace: 'nowrap' }}>{baseUnitPriceLabel(line)}</div>
                      )}
                    </div>
                  )}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'right' }}>
                  {formatPrice(lineAmount(line))}
                </td>
                <td style={{ padding: '6px 8px 6px 0', textAlign: 'center' }}>
                  {isEditable && (
                    <button type="button" onClick={() => handleRemoveLine(line.key)} title="Remove line" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#b91c1c' }}>×</button>
                  )}
                </td>
              </tr>
            ))}

            {isEditable && (
              <tr>
                <td style={{ padding: '6px 10px 6px 0', color: '#888', textAlign: 'right' }}>{lines.length + 1}</td>
                <td colSpan={2} style={{ padding: '6px 8px 6px 0', position: 'relative' }}>
                  <input
                    className="styled-input"
                    placeholder={customerId ? 'Search name or SKU...' : 'Select a Customer first'}
                    disabled={!customerId}
                    value={draftRow.productSearch}
                    onChange={(e) => handleDraftSearchChange(e.target.value)}
                    onFocus={() => { if (draftRow.results.length) setDraftRow((prev) => ({ ...prev, showResults: true })); }}
                    onBlur={() => setTimeout(() => setDraftRow((prev) => ({ ...prev, showResults: false })), 150)}
                    style={{ fontSize: 12, width: '100%' }}
                  />
                  {draftRow.showResults && draftRow.results.length > 0 && (
                    <ul style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, width: 320, margin: 0, padding: 4, listStyle: 'none', background: '#fff', border: '1px solid #bfc8d6', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                      {draftRow.results.map((p) => (
                        <li
                          key={p.id}
                          onMouseDown={() => handleDraftSelectProduct(p)}
                          style={{ padding: '6px 8px', cursor: 'pointer', fontSize: 13, borderRadius: 4 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#e3eaf3'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          {productLabel(p)}
                        </li>
                      ))}
                    </ul>
                  )}
                </td>
                <td colSpan={4} />
              </tr>
            )}
          </tbody>
        </table>
      </Accordion>
      </div>

      {/* Comments and Totals start collapsed to keep the lines in view;
          the header still shows the Total, ex or inc GST following the
          switch like the Lines header's Subtotal. */}
      <div>
      <Accordion
        title="Comments & Totals"
        defaultOpen={false}
        showMenuPlaceholder={false}
        headerExtra={(
          <span style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14 }}>
            <span style={{ fontWeight: 700 }}>Total{basisSuffix} {formatPrice(gstApplicable && !viewInc ? Math.round((totals.total - totals.gst) * 100) / 100 : totals.total)}</span>
          </span>
        )}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 72 }}>
          <div style={{ flex: 1 }}>
            <DocumentComments
              customerComment={customerComment}
              onCustomerCommentChange={setCustomerComment}
              staffComment={staffComment}
              onStaffCommentChange={setStaffComment}
              customerHint="Shown to the customer on the print"
              disabled={!isEditable}
            />
            {(priceReminder || showShippingDisclaimer || showPendingCostDisclaimer || showEstimateWording) && (
              <div style={{ fontSize: 12, color: '#666', borderTop: '1px solid #ddd', paddingTop: 10, marginTop: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#888', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                {priceReminder && <div style={{ whiteSpace: 'pre-wrap' }}>{fillQuoteReminder(priceReminder, { validUntil: quoteValidUntil({ status: quoteMeta?.status, valid_until: quoteMeta?.validUntil }, validDays) })}</div>}
                {showShippingDisclaimer && <div><sup>1</sup> {showEstimateWording ? joinShippingNotes(selectedShippingService.disclaimer, estimateWording) : selectedShippingService.disclaimer}</div>}
                {showPendingCostDisclaimer && <div><sup>2</sup> {selectedShippingService.pending_cost_disclaimer}</div>}
                {showEstimateWording && !showShippingDisclaimer && <div><sup>*</sup> {estimateWording}</div>}
              </div>
            )}
          </div>
          {/* While editable, the totals keep a 20px column on the right for
              the Shipping charge's revert badge (#437), so every figure
              stays lined up whether or not the badge shows. */}
          <div style={{ minWidth: 240, paddingRight: isEditable ? 20 : 0 }}>
            {anyNeedsPrice && (
              <div style={{ fontSize: 12, color: '#b45309', marginBottom: 8, textAlign: 'right' }}>
                One or more lines need a price
              </div>
            )}
            {/* Trailing hidden spacer matches GstAwareCostField's calculator-
                button width+gap (15px + 4px) below, so Subtotal/GST/Total's
                own right-aligned figures land in the same column as
                Shipping's typed value rather than sitting 19px further right
                (where its calculator button pushes them). */}
            {/* Subtotal (ex or inc GST, following the switch), Shipping,
                Options, GST, Total (inc GST) — the same for Business and
                Retail. Retail without GST shows only the Total. */}
            {(gstApplicable || !basis.retail) && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>Subtotal{basisSuffix}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(viewSubtotal(lines, viewInc))}
                  {isEditable && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            <OrderDiscountRow
              discount={discount}
              amount={viewDiscount(lines, totals.discount, basis.retail, viewInc)}
              basisSuffix={basisSuffix}
              entryBasis={gstApplicable ? (basis.retail ? 'inc GST' : 'ex GST') : ''}
              locked={!isEditable}
              gutter={isEditable ? 19 : 0}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, padding: '4px 0', gap: 8 }}>
              <span>
                Shipping{basisSuffix}
                {showShippingDisclaimer && <sup style={{ marginLeft: 2 }}>1</sup>}
                {showPendingCostDisclaimer && <sup style={{ marginLeft: 2 }}>2</sup>}
              </span>
              {/* The revert badge sits in the totals' right-hand column, so the charge
                  still lines up with the amounts above and below. */}
              <span style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <GstAwareCostField
                  value={shippingCost}
                  onCommit={setShippingCost}
                  gstApplicable={viewInc}
                  gstRate={gstRate}
                  locked={!isEditable}
                />
                {typedOver && isEditable && (
                  <span style={{ position: 'absolute', left: '100%', marginLeft: 4, display: 'flex' }}>
                    <OverrideRevertBadge
                      onClick={() => { setShippingCost(Number(estimate.charge)); setShippingOverrideReason(''); }}
                      title="Overridden — click to restore the estimate"
                    />
                  </span>
                )}
              </span>
            </div>
            {estimate && (
              <div style={{ fontSize: 11, color: '#5a6b85', textAlign: 'right', marginTop: -2, marginBottom: 4, paddingRight: isEditable ? 19 : 0 }}>
                {typedOver ? 'Estimate Overridden' : 'Estimate'}
              </div>
            )}
            {typedOver && <ShippingOverrideReason value={shippingOverrideReason} onChange={setShippingOverrideReason} locked={!isEditable} />}
            {showPendingCost && (
              <div style={{ fontSize: 11, color: '#b45309', textAlign: 'right', marginTop: -2, marginBottom: 4, paddingRight: isEditable ? 19 : 0 }}>
                To be confirmed
              </div>
            )}
            {selectedOptions.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>Options Cost{basisSuffix}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(viewInc ? totals.options : Math.round(optionsCostEx * 100) / 100)}
                  {isEditable && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            {gstApplicable && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '4px 0' }}>
                <span>{viewInc ? 'Includes GST' : 'GST'}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {formatPrice(totals.gst)}
                  {isEditable && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 16, fontWeight: 700, padding: '8px 0', borderTop: '1px solid #b3bbc7', marginTop: 4 }}>
              <span>Total{gstApplicable ? ' (inc GST)' : ''}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {formatPrice(totals.total)}
                {isEditable && <span aria-hidden="true" style={{ width: 15, visibility: 'hidden' }} />}
              </span>
            </div>
            {lines.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#5a6b85', padding: '4px 0' }}>
                <span>Total Weight</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, paddingRight: isEditable ? 19 : 0 }}>
                  {anyMissingWeight && <NeedsWeightPill />}
                  {formatWeight(totalWeight)}
                </span>
              </div>
            )}
          </div>
        </div>
      </Accordion>
      </div>

      {id && (
        <DocumentPayments
          basePath="quotes"
          documentId={id}
          recordOpen={recordPaymentOpen}
          onRecordOpenChange={setRecordPaymentOpen}
          onSummary={setPaymentSummary}
          onChanged={handlePaymentsChanged}
          canUpdate={canUpdate}
          reloadKey={quoteMeta}
        />
      )}

      <div style={{ marginTop: 16 }}>
        {isEditable && (
          <>
            <SaveButton type="button" onClick={() => handleSave(false)} loading={saveMode === 'stay'} disabled={!customerId || saving} />
            <SaveButton type="button" onClick={() => handleSave(true)} loading={saveMode === 'close'} disabled={!customerId || saving} style={{ marginLeft: 8 }}>
              {saveMode === 'close' ? 'Saving...' : 'Save & Close'}
            </SaveButton>
            <CancelButton onClick={handleCancel} disabled={saving} style={{ marginLeft: 8 }} />
          </>
        )}
        {!isEditable && <CancelButton onClick={handleCancel} disabled={saving}>Close</CancelButton>}
      </div>

      {showReport && (
        <ReportOverlay onClose={() => setShowReport(false)}>
          <QuoteReportView quoteId={id} onClose={() => setShowReport(false)} />
        </ReportOverlay>
      )}

      {id && customer && (
        <ComposeEmailDrawer
          open={showEmail}
          onOpenChange={setShowEmail}
          entity={{ id: customer.id, name: customer.label }}
          quoteId={id}
          onSaved={handleEmailed}
        />
      )}

      {reestimateDialog}

      {convertDialog && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next && !converting) setConvertDialog(null); }}
          title="Convert to Sales Order"
          description={`Convert ${quoteMeta?.display_number} to a Sales Order?`}
          footer={(
            <>
              <CancelButton onClick={() => setConvertDialog(null)} disabled={converting} />
              <button
                type="button"
                onClick={() => convertQuote(convertDialog)}
                disabled={converting}
                style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                {converting ? 'Converting...' : 'Convert'}
              </button>
            </>
          )}
        >
          {customerComment.trim() && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
              <input
                type="checkbox"
                checked={convertDialog.copy_customer_comment}
                onChange={(e) => setConvertDialog((prev) => ({ ...prev, copy_customer_comment: e.target.checked }))}
              />
              Copy Customer Comment
            </label>
          )}
          {staffComment.trim() && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={convertDialog.copy_staff_comment}
                onChange={(e) => setConvertDialog((prev) => ({ ...prev, copy_staff_comment: e.target.checked }))}
              />
              Copy Staff Comment
            </label>
          )}
        </ConfirmDialog>
      )}

      {copyDialog && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next) setCopyDialog(null); }}
          title="Copy Quote"
          description={`Copy ${quoteMeta?.display_number} to a new Quote, priced fresh.`}
          footer={(
            <>
              <CancelButton onClick={() => setCopyDialog(null)} />
              <button
                type="button"
                onClick={() => startCopy(copyDialog.withdraw ? { note: copyDialog.note.trim() } : null)}
                style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                Copy
              </button>
            </>
          )}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={copyDialog.withdraw}
              onChange={(e) => setCopyDialog((prev) => ({ ...prev, withdraw: e.target.checked }))}
            />
            Withdraw {quoteMeta?.display_number} when the copy is saved
          </label>
          {copyDialog.withdraw && (
            <label style={{ fontSize: 13 }}>
              Note (optional)
              <textarea
                className="styled-input"
                value={copyDialog.note}
                onChange={(e) => setCopyDialog((prev) => ({ ...prev, note: e.target.value }))}
                rows={3}
                style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, resize: 'vertical' }}
              />
              <span style={{ display: 'block', marginTop: 4, color: '#888', fontSize: 12 }}>
                The withdrawal note starts with &ldquo;Replaced by&rdquo; and the new Quote&rsquo;s number.
              </span>
            </label>
          )}
        </ConfirmDialog>
      )}

      {closeAction && (
        <ConfirmDialog
          open
          onOpenChange={(next) => { if (!next) resetCloseDialog(); }}
          title={CLOSE_ACTIONS[closeAction].title}
          description={`${quoteMeta?.display_number} will be marked ${CLOSE_ACTIONS[closeAction].statusName} and kept as a historical record. This can't be undone.`}
          footer={(
            <>
              <CancelButton onClick={resetCloseDialog} disabled={closing} />
              <button
                type="button"
                onClick={handleConfirmClose}
                disabled={closing || !closeDate}
                style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#b91c1c', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
              >
                {closing ? CLOSE_ACTIONS[closeAction].busy : CLOSE_ACTIONS[closeAction].confirm}
              </button>
            </>
          )}
        >
          <label style={{ fontSize: 13, display: 'block', marginBottom: 12 }}>
            {CLOSE_ACTIONS[closeAction].dateLabel}
            <DateInput
              className="styled-input"
              value={closeDate}
              max={todayISO}
              onChange={(e) => setCloseDate(e.target.value)}
              style={{ display: 'block', width: 150, marginTop: 4, fontSize: 13 }}
            />
          </label>
          <label style={{ fontSize: 13 }}>
            Note (optional)
            <textarea
              className="styled-input"
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              rows={3}
              style={{ display: 'block', width: '100%', marginTop: 4, fontSize: 13, resize: 'vertical' }}
            />
          </label>
        </ConfirmDialog>
      )}
    </div>
    {/* Remounted on a status change too (Send, Accept, Withdraw...). */}
    <QuoteTimeline
      key={`timeline-${historyVersion}-${quoteMeta?.status}`}
      quoteMeta={quoteMeta}
      documentId={id}
    />
    </div>
  );
}

export default QuotePage;
